import { ColumnsType } from 'antd/es/table'
import { saveAs } from 'file-saver'
import moment from 'moment'

import { SumDataEntry } from '../csv/exportToCSV'
import formatValueCsv, { ExportColumn } from '../exportUtils'
import processRows from '../processRows'
import toXLSBlob from './toXLSBlob'

type CellValue = string | number

interface SummarySheet {
  title?: string
  headers: string[]
  rows: CellValue[][]
}

interface ExportToXLSParams<T extends object> {
  columns: ColumnsType<T>
  dataSource: T[]
  reportName: string
  exportDetails?: boolean
  sumData?: SumDataEntry[]
}

const processSummaryRows = (sumData: SumDataEntry[] = []): SummarySheet[] => {
  if (sumData.length === 0) return []

  return sumData.map((summary) => {
    const headers = summary.columns.map((c) => c.title)
    const rows = summary.dataSource.map((record) =>
      summary.columns.map((col) => formatValueCsv(record[col.dataIndex], col, record)),
    )
    return { title: summary.title, headers, rows }
  })
}

const generateCellHtml = (cell: CellValue): string => `<td>${String(cell)}</td>`

const generateRowHtml = (row: CellValue[]): string => `<tr>${row.map(generateCellHtml).join('')}</tr>`

const generateHeaderCellHtml = (header: string): string => `<th>${header}</th>`

const generateHeaderHtml = (headers: string[]): string =>
  headers.length > 0 ? `<tr>${headers.map(generateHeaderCellHtml).join('')}</tr>` : ''

const generateHTMLTable = (headers: string[], rows: CellValue[][], title?: string): string => {
  const titleHtml = title
    ? `<tr><td colspan="${String(headers.length)}" style="font-weight:bold;">${title}</td></tr>`
    : ''
  const headerHtml = generateHeaderHtml(headers)
  const bodyHtml = rows.map(generateRowHtml).join('')

  return `
    <table>
      <thead>
        ${titleHtml}
        ${headerHtml}
      </thead>
      <tbody>${bodyHtml}</tbody>
    </table>
  `
}

const exportToXLS = <T extends object>({
  columns,
  dataSource,
  reportName,
  exportDetails = false,
  sumData = [],
}: ExportToXLSParams<T>): void => {
  const cols = columns as unknown as ExportColumn[]

  const detailColumnDef = cols.find((col) => col.columns)
  const mainColumns = cols.filter((col) => (!col.columns || col.columns.length === 0) && col.exportable !== false)
  const detailColumns = detailColumnDef?.columns
    ? detailColumnDef.columns.filter((col) => col.exportable !== false)
    : []
  const activeColumns = exportDetails ? [...mainColumns, ...detailColumns] : mainColumns

  const mainHeaders = activeColumns.map((col) => col.title)
  const mainRows = processRows({
    dataSource: dataSource as unknown as Record<string, unknown>[],
    mainColumns,
    detailColumns,
    detailDataIndex: detailColumnDef?.dataIndex ?? 'detalhes',
    activeColumns,
    exportDetails,
  })

  const mainTableHtml = generateHTMLTable(mainHeaders, mainRows)
  const summaries = processSummaryRows(sumData)
  const summaryTablesHtml = summaries.map((s) => `<br/><br/>${generateHTMLTable(s.headers, s.rows, s.title)}`).join('')

  const finalContent = `${mainTableHtml}${summaryTablesHtml}`
  const reportTimeStamp = moment().format('DD_MM_YYYY_HH_mm_ss')

  const blob = toXLSBlob(finalContent)
  saveAs(blob, `relatorio_${reportName}_${reportTimeStamp}.xls`)
}

export default exportToXLS
